import React, { memo, useState } from "react";
import { Form, Dropdown, Table, Card, Button, Badge } from "tabler-react";
import { DateTime } from "luxon";
import truncate from "../utils/truncate";
import { sourceLabel } from "../utils/source";
import { getAuthToken } from "../auth/Auth";

function useUpdateColumns({ allColumns }) {
  const [currentColumns, setColumns] = useState(allColumns);

  function handleUpdateColumns({ target: { value } }) {
    if (currentColumns.includes(value)) {
      setColumns(currentColumns.filter((column) => column !== value));
    } else {
      setColumns(
        allColumns.filter((column) =>
          [...currentColumns, value].includes(column)
        )
      );
    }
  }

  return { currentColumns, handleUpdateColumns };
}

const COLUMNS = [
  "Photo",
  "Description",
  "Amazon",
  "Condition",
  "Location",
  "Ends At",
  "Price",
  "MSRP",
];

const ColumnOption = ({ onChange, currentColumns, column }) => (
  <Form.Checkbox
    checked={currentColumns.includes(column)}
    label={column}
    name="column"
    onChange={onChange}
    value={column}
    className="ml-2 center"
  />
);

function Column({ column, item }) {
  let content;
  if (column === "Photo") {
    content = (
      <div className="w-8">
        <a
          target="_blank"
          href={item.itemUrl}
          onClick={(e) => e.stopPropagation()}
        >
          <img src={item.imageUrl} alt={`Preview of item`} />
        </a>
      </div>
    );
  } else if (column === "Description") {
    content =
      item.descriptionData && truncate(item.descriptionData.description);
  } else if (column === "Amazon") {
    content = (
      <a
        href={`https://www.amazon.com/s?k=${encodeURIComponent(
          item.descriptionData.description
        )}&tag=ftasearch-20`}
        onClick={(e) => e.stopPropagation()}
        target="_blank"
      >
        <Button color="secondary">{`🔍 Amazon`}</Button>
      </a>
    );
  } else if (column === "Condition") {
    content = item.descriptionData && item.descriptionData.condition;
  } else if (column === "Location") {
    content = (
      <React.Fragment>
        {item.location}
        {sourceLabel(item.source) && (
          <Badge color="secondary" className="ml-1">
            {sourceLabel(item.source)}
          </Badge>
        )}
      </React.Fragment>
    );
  } else if (column === "Ends At") {
    content = DateTime.fromJSDate(new Date(item.endsAt)).toFormat("D");
  } else if (column === "Price") {
    content = <strong>{`$${item.lastHighBid}`}</strong>;
  } else if (column === "MSRP") {
    content = item.descriptionData && (
      <strong>{`$${item.descriptionData.msrp}`}</strong>
    );
  }

  return (
    <Table.Col>
      {content}
      {column === "Ends At" && item.ended === false && (
        <Badge color="success" className="mr-1 d-block">
          Active
        </Badge>
      )}
    </Table.Col>
  );
}

const Item = ({ currentColumns = [], item, onClick = () => ({}) }) => (
  <Table.Row onClick={() => onClick(item)}>
    {currentColumns.map((column) => (
      <Column key={column} column={column} item={item} />
    ))}
  </Table.Row>
);

const ItemTable = memo(
  ({
    csvUrl,
    stats,
    items,
    onClick,
    prevPage = () => ({}),
    nextPage = () => ({}),
    lastPage = false,
    firstPage = false,
  }) => {
    const fetchCsv = async () => {
      try {
        const result = await fetch(`https://api.bidft.auction${csvUrl}`, {
          headers: {
            Authorization: getAuthToken(),
          },
          mode: "cors",
        });
        console.log(result);
      } catch (e) {
        console.log(e);
      }
    };

    const { currentColumns, handleUpdateColumns } = useUpdateColumns({
      allColumns: COLUMNS,
    });
    return (
      <React.Fragment>
        <Card.Header>
          <Card.Title>
            {stats && (
              <React.Fragment>
                Average Price:{" "}
                <strong className="pr-3">
                  ${Math.floor(stats.averagePrice * 100) / 100}
                </strong>
                Count:
                <strong>{stats.count}</strong>
              </React.Fragment>
            )}
          </Card.Title>
          <Card.Options>
            {csvUrl && <Button onClick={fetchCsv}>Download CSV</Button>}
            <Dropdown
              type="button"
              toggle={false}
              icon="settings"
              triggerContent="Columns"
              items={[
                <Form.Group key="column-select">
                  {COLUMNS.map((columnName) => (
                    <ColumnOption
                      key={columnName}
                      onChange={handleUpdateColumns}
                      column={columnName}
                      currentColumns={currentColumns}
                    />
                  ))}
                </Form.Group>,
              ]}
            />
          </Card.Options>
        </Card.Header>
        <Table
          className={`card-table table-outline table-vcenter ${
            onClick ? "table-hover" : ""
          }`}
        >
          <Table.Header>
            <Table.Row>
              {currentColumns.map((column) => (
                <Table.ColHeader key={column}>{column}</Table.ColHeader>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {items.map((item) => (
              <Item
                key={item.id}
                currentColumns={currentColumns}
                item={item}
                onClick={onClick}
              />
            ))}
          </Table.Body>
        </Table>
        <Card.Footer className="d-flex justify-content-between">
          <Button disabled={firstPage} onClick={prevPage}>
            Previous Page
          </Button>
          <Button disabled={lastPage} onClick={nextPage}>
            Next Page
          </Button>
        </Card.Footer>
      </React.Fragment>
    );
  }
);

export default ItemTable;



// WEBPACK FOOTER //
// ./src/components/ItemTable.js